import { RefObject, useCallback, useEffect, useMemo, useState } from "react"
import { UseCounterProps } from "@chakra-ui/react"
import { Statistics } from "@mezo/api"
import { SupportedTokenName } from "@mezo/sdk"
import { useQueryClient } from "@tanstack/react-query"
import { Address, encodeFunctionData, erc20Abi, erc721Abi } from "viem"
import {
  useAccount,
  useEstimateGas,
  useGasPrice,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi"

import {
  selectCollateralPrice,
  selectDepositedTokens,
  selectMezoWalletBalances,
  selectPortalStatistics,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import {
  ALL_TOKENS,
  BITCOIN_TOKENS,
  FLOATING_POINT_REGEX,
  FRIENDLY_DECIMALS_AMOUNT,
  MEZO_CHAIN,
  ONE_SECOND_IN_MILLISECONDS,
  PORTAL_TOKENS,
  SPECIAL_PRICE_TOKENS,
} from "#/shared/constants"
import { PortalToken } from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  getCurrencySymbol,
  multiplyBigIntWithDecimal,
  trimDecimals,
  validateMaximumTokenAmount,
  validateMinimumTokenAmount,
} from "#/shared/utils"

import { USDT } from "../constants/artifacts"

import { useDropdown } from "./helpers"

const refetchInterval = ONE_SECOND_IN_MILLISECONDS * 10 // 10 seconds
const staleTime = refetchInterval

export const useTokenSelect = (
  tokenSelectRef: RefObject<HTMLDivElement>,
  activeToken?: PortalToken<SupportedTokenName>,
  callback?: (token: SupportedTokenName) => void,
) => {
  const [selectedToken, setSelectedToken] = useState(activeToken)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const closeDropdownHandler = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const { dropdownRef } = useDropdown(
    tokenSelectRef.current,
    closeDropdownHandler,
  )

  useEffect(() => {
    if (activeToken !== selectedToken) {
      setSelectedToken(activeToken)
    }
  }, [activeToken, selectedToken])

  const updateSelectedTokenHandler = useCallback(
    (token: PortalToken<SupportedTokenName>) => {
      setIsDropdownOpen(false)
      setSelectedToken(token)
      callback?.(token.token)
    },
    [callback],
  )

  return {
    selectedToken,
    updateSelectedToken: updateSelectedTokenHandler,
    isDropdownOpen,
    setIsDropdownOpen,
    dropdownRef,
  }
}

export const useTokenAmountInput = (
  tokenAmount: string,
  setAmount: (amount: string) => void,
  token: SupportedTokenName | undefined = undefined,
  /**
   * Minimum value that will pass validation. In token (user) unit.
   */
  minTokenAmount?: string,
  /**
   * Maximum value that will fail validation after that. In token (user) unit.
   */
  maxTokenAmount?: string,
  tokenDecimals = 18,
  precisionAmount = 18,
  additionalValidationCallback = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    input: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    min?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    max?: string,
  ): { isValid: boolean; errorMessage: string | JSX.Element } => ({
    isValid: true,
    errorMessage: "",
  }),
): {
  handleTokenAmountChange: (
    valueAsString: string,
    valueAsNumber: number,
  ) => void
  showError: boolean
  errorMessage: string | JSX.Element | null
} => {
  const [isValidAmount, setIsValidAmount] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string | JSX.Element | null>(
    null,
  )

  const checkValidNumberOfDecimals = useCallback(
    (input: string) => {
      if (!input) return true

      const checkRegex = new RegExp(`^(?!0\\d)\\d+\\.?\\d{0,${tokenDecimals}}$`)

      return checkRegex.test(input)
    },
    [tokenDecimals],
  )

  const handleTokenAmountValidation = useCallback(
    (input: string) => {
      if (!input) {
        setErrorMessage(null)
        return
      }

      const isHigherOrEqualMinValue = minTokenAmount
        ? validateMinimumTokenAmount(
            floatToBigInt(input),
            floatToBigInt(minTokenAmount),
          )
        : true

      const isLowerOrEqualMaxValue = maxTokenAmount
        ? validateMaximumTokenAmount(
            floatToBigInt(input),
            floatToBigInt(maxTokenAmount),
          )
        : true

      const {
        isValid: hasAdditionalValidationPassed,
        errorMessage: customErrorMessage,
      } = additionalValidationCallback(input, minTokenAmount, maxTokenAmount)

      setIsValidAmount(
        isLowerOrEqualMaxValue &&
          isHigherOrEqualMinValue &&
          hasAdditionalValidationPassed,
      )

      let newErrorMessage: string | JSX.Element | null = null

      if (!hasAdditionalValidationPassed) {
        newErrorMessage = customErrorMessage
      } else if (!isHigherOrEqualMinValue && minTokenAmount) {
        newErrorMessage =
          minTokenAmount !== "0"
            ? `Minimum amount is ${minTokenAmount}`
            : "Amount must be greater than 0"
      } else if (!isLowerOrEqualMaxValue) {
        newErrorMessage = token
          ? `Not enough ${getCurrencySymbol(token)} in your wallet`
          : `Maximum amount is ${maxTokenAmount}`
      }

      setErrorMessage(newErrorMessage)
    },
    [minTokenAmount, maxTokenAmount, additionalValidationCallback, token],
  )

  const handleTokenAmountChange: UseCounterProps["onChange"] = (
    valueAsString,
  ) => {
    const tokenAmountWithoutCommas = valueAsString.replace(/,/g, "")

    const trimmedValue = trimDecimals(
      tokenAmountWithoutCommas.toString(),
      precisionAmount,
    )

    const isFloatingPoint =
      trimmedValue.length === 0 || FLOATING_POINT_REGEX.test(trimmedValue)

    const hasValidNumberOfDecimals = checkValidNumberOfDecimals(trimmedValue)

    if (!isFloatingPoint || !hasValidNumberOfDecimals) return

    setAmount(trimmedValue)
    handleTokenAmountValidation(trimmedValue)
  }

  useEffect(() => {
    handleTokenAmountValidation(tokenAmount)
  }, [tokenAmount, handleTokenAmountValidation])

  const showError = useMemo(
    () => !!(!isValidAmount && tokenAmount),
    [isValidAmount, tokenAmount],
  )

  return { handleTokenAmountChange, showError, errorMessage }
}

export const useFriendlyTokenAmount = (amount: string) => {
  const hasManyDecimals = useMemo(() => {
    const decimalsPart = amount.split(".")[1]

    if (!decimalsPart) return false

    return decimalsPart.length > FRIENDLY_DECIMALS_AMOUNT
  }, [amount])

  const parsedDepositAmount = useMemo(() => {
    const [integerPart, decimalsPart] = amount.split(".")

    return decimalsPart
      ? `${integerPart}.${decimalsPart.slice(0, FRIENDLY_DECIMALS_AMOUNT)}`
      : integerPart
  }, [amount])

  const isSmallAmount = Number(amount) < 10 ** -FRIENDLY_DECIMALS_AMOUNT

  const formattedAmount =
    isSmallAmount && amount !== "0" ? "<1" : parsedDepositAmount

  const displayedDepositAmount = hasManyDecimals ? formattedAmount : amount

  return { hasManyDecimals, displayedDepositAmount }
}

export const useAvailableTokens = () => {
  const walletBalances = useDappSelector(selectWalletBalances)

  const availableTokens = useMemo(() => {
    const availabeTokens = []

    // eslint-disable-next-line no-restricted-syntax
    for (const token of PORTAL_TOKENS) {
      if (walletBalances[token.token].balance > 0n) {
        availabeTokens.push(token)
      }
    }

    return availabeTokens
  }, [walletBalances])

  return { availableTokens }
}

export const useAvailableMezoTokens = () => {
  const walletBalances = useDappSelector(selectMezoWalletBalances)

  const availableTokens = useMemo(() => {
    const availabeTokens = []

    // eslint-disable-next-line no-restricted-syntax
    for (const token of ALL_TOKENS) {
      if (
        walletBalances[token.token] &&
        // @ts-expect-error FIXME: Figure out why this is throwing type error
        walletBalances[token.token].balance > 0n
      ) {
        availabeTokens.push(token)
      }
    }

    return availabeTokens
  }, [walletBalances])

  return { availableTokens }
}

export const useDepositedTokens = () => {
  const depositedTokens = useDappSelector(selectDepositedTokens)

  const depositTokens = useMemo(() => {
    const tokens = []

    // eslint-disable-next-line no-restricted-syntax
    for (const token of PORTAL_TOKENS) {
      if (depositedTokens.includes(token.token)) {
        tokens.push(token)
      }
    }

    return tokens
  }, [depositedTokens])

  return { depositTokens }
}

export const useERC20TokenBalance = (
  tokenAddress: Address,
  owner: Address | undefined,
  chainId = MEZO_CHAIN.id,
) =>
  useReadContract({
    abi: erc20Abi,
    functionName: "balanceOf",
    address: tokenAddress,
    args: [owner!],
    query: { enabled: !!owner },
    chainId,
  })

export const useAllowanceERC20Token = (
  tokenAddress: Address,
  spender: Address,
  chainId: number = MEZO_CHAIN.id,
) => {
  const { address } = useAccount()
  return useReadContract({
    abi: erc20Abi,
    address: tokenAddress,
    functionName: "allowance",
    args: [address!, spender],
    query: { enabled: !!address },
    chainId,
  })
}

export const useApproveERC20Token = (
  tokenAddress: Address,
  spender: Address,
  chainId: number = MEZO_CHAIN.id,
) => {
  const {
    writeContractAsync,
    data: approveTxHash,
    ...rest
  } = useWriteContract()

  const queryClient = useQueryClient()

  const { isLoading: isWaitingForTxReceipt, isSuccess: isTxConfirmed } =
    useWaitForTransactionReceipt({
      hash: approveTxHash,
      chainId,
    })

  const {
    data: approvedAmount,
    queryKey,
    isFetching: isFetchingApprovedAmount,
    refetch: refetchApprovedAmount,
  } = useAllowanceERC20Token(tokenAddress, spender, chainId)

  useEffect(() => {
    if (isTxConfirmed) {
      refetchApprovedAmount()
    }
  }, [isTxConfirmed, refetchApprovedAmount])

  const approve = useCallback(
    async (amount: bigint) => {
      const allowance = await queryClient.fetchQuery<bigint>({ queryKey })
      if (allowance >= amount) return

      await writeContractAsync({
        abi: tokenAddress === USDT.address ? erc721Abi : erc20Abi,
        address: tokenAddress,
        functionName: "approve",
        args: [spender, amount],
        chainId,
      })
    },
    [writeContractAsync, tokenAddress, spender, queryKey, queryClient, chainId],
  )

  return {
    approve,
    approvedAmount,
    isFetchingApprovedAmount,
    isWaitingForTxReceipt,
    isTxConfirmed,
    approveTxHash,
    ...rest,
  }
}

export const useEstimateApproveERC20TokenGas = (
  tokenAddress: Address,
  spender: Address,
  amount: bigint,
  chainId: number = MEZO_CHAIN.id,
) => {
  const statistics = useDappSelector(selectPortalStatistics)
  const currentUsdPerEth = statistics?.currentUsdPerEth ?? 0

  const { data: gasAmount, isFetching: isFetchingGasAmount } = useEstimateGas({
    to: tokenAddress,
    data: encodeFunctionData({
      abi: erc20Abi,
      functionName: "approve",
      args: [spender, amount],
    }),
    chainId,
    query: {
      staleTime,
      refetchInterval,
    },
  })

  const { data: gasPrice, isFetching: isFetchingGasPrice } = useGasPrice({
    chainId,
    query: {
      enabled: !!gasAmount,
      staleTime,
      refetchInterval,
    },
  })

  const estimatedTransactionFee = (gasAmount ?? 0n) * (gasPrice ?? 0n)

  const estimatedTransactionFeeInUSD =
    Number(bigIntToFloat(estimatedTransactionFee ?? 0n, 18, 7)) *
    currentUsdPerEth

  return {
    isFetching: isFetchingGasAmount || isFetchingGasPrice,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee,
    estimatedTransactionFeeInUSD,
  }
}

export const useTokenUsdPrice = () => {
  const statistics = useDappSelector(selectPortalStatistics)
  const collateralPrice = useDappSelector(selectCollateralPrice)
  const { mBTC } = useDappSelector(selectWalletBalances)

  // - For BTC-like tokens we are using the same price as BTC
  // - For stablecoins we are using 1:1 ratio with USD
  // - For special case tokens we use another price
  const getTokenUsdPrice = useCallback(
    (token: SupportedTokenName) => {
      if (!statistics) return 0

      if (token === "mBTC") {
        return Number(
          bigIntToFloat(
            collateralPrice ?? 0n,
            Number(mBTC.decimals),
            Number(mBTC.decimals),
          ),
        )
      }

      if (SPECIAL_PRICE_TOKENS[token]) {
        return Number(
          statistics[SPECIAL_PRICE_TOKENS[token] as keyof Statistics] ?? "0",
        )
      }

      return BITCOIN_TOKENS.some(({ token: tokenName }) => tokenName === token)
        ? Number(statistics.currentUsdPerBtc)
        : 1
    },
    [statistics, collateralPrice, mBTC],
  )

  const getTokenUsdValueLabel = useCallback(
    (amount: bigint, token: SupportedTokenName, decimals: number) => {
      const tokenPrice = getTokenUsdPrice(token)

      return bigIntToHumanReadableFormat(
        multiplyBigIntWithDecimal(amount, Number(tokenPrice)),
        decimals,
        FRIENDLY_DECIMALS_AMOUNT,
      )
    },
    [getTokenUsdPrice],
  )

  return { getTokenUsdPrice, getTokenUsdValueLabel }
}

export const useBTCToUSDConversion = (value: string | number) => {
  const { getTokenUsdPrice } = useTokenUsdPrice()

  return useMemo(
    () => Number(value) * getTokenUsdPrice("BTC"),
    [getTokenUsdPrice, value],
  )
}
